import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Gift, ShieldCheck, Loader2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useGated } from "@/hooks/usePublicConfig";
import { WaitlistForm } from "@/components/lander/WaitlistForm";
import { storeInvite, clearStoredInvite } from "@/lib/referral";

const FRIEND_PERK_DAYS = 7;

// Human landing for /i/{code} (browser fallback of the backend /api/i/{code} invite link).
// Shows the joiner-side perk, persists the code, and routes into signup — or connects an
// already-signed-in user with their inviter.
export default function InviteLanding() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const gated = useGated();
  const clean = (code || "").trim().toUpperCase();
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    if (!clean) {
      navigate("/", { replace: true });
      return;
    }
    storeInvite(clean);
  }, [clean, navigate]);

  useEffect(() => {
    if (loading || !clean || !user || redeeming) return;
    setRedeeming(true);
    api
      .redeemInvite(clean)
      .then((d) => {
        const who = d?.connected_with?.display_name || d?.inviter?.display_name || d?.invite_connected_with?.display_name;
        toast.success(who ? `Connected with ${who}` : "Invite accepted");
      })
      .catch(() => {})
      .finally(() => {
        clearStoredInvite();
        navigate("/app/chats", { replace: true });
      });
  }, [loading, user, clean, redeeming, navigate]);

  if (loading || (user && redeeming)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bloq-bg" data-testid="invite-landing-loading">
        <Loader2 className="h-7 w-7 animate-spin text-bloq-mint" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bloq-bg px-5 py-10" data-testid="invite-landing">
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-bloq-mint/25 bg-gradient-to-br from-bloq-mint/[0.08] to-bloq-purple/[0.06] p-7 text-center">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-bloq-mint/15 text-bloq-mint">
          <Gift className="h-7 w-7" />
        </span>
        <h1 className="mt-4 font-heading text-2xl font-bold text-white">You've been invited to BLOQ Talk</h1>
        <p className="mt-2 text-sm text-bloq-muted">
          Join with this invite and <span className="font-semibold text-white">you both get {FRIEND_PERK_DAYS} days of BLOQ+ free</span> — the trust-gated messenger for Web3.
        </p>
        <div className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-white/12 bg-bloq-bg/60 px-3 py-1.5 font-mono text-sm tracking-widest text-bloq-mint" data-testid="invite-landing-code">
          <ShieldCheck className="h-3.5 w-3.5" /> {clean}
        </div>
        {gated ? (
          <div className="mt-6" data-testid="invite-landing-waitlist">
            <div className="mb-4 flex items-center justify-center gap-2 rounded-full border border-bloq-mint/30 bg-bloq-mint/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-bloq-mint">
              <ShieldCheck className="h-3.5 w-3.5" /> Currently in private beta testing
            </div>
            <WaitlistForm showCounter={false} stacked buttonLabel="Join the waitlist" refCode={clean} />
            <p className="mt-3 text-xs text-bloq-muted">
              BLOQ Talk is invite-only right now. Join the waitlist and you'll get access the moment the beta opens.
            </p>
          </div>
        ) : (
          <>
            <Link
              to={`/signup?code=${encodeURIComponent(clean)}`}
              className="mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-xl bloq-gradient text-sm font-bold text-bloq-bg transition-opacity hover:opacity-90"
              data-testid="invite-landing-accept"
            >
              Accept invite & sign up <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/login" className="mt-3 inline-block text-xs text-bloq-muted hover:text-white" data-testid="invite-landing-login">
              Already have an account? Log in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
